import { Alert, Grid } from '@mantine/core';
import { useEffect, useRef, useState, useContext } from 'react';
import ProductSearcher from './components/ProductSearcher';
import ProductList from './components/ProductList';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import useValidStoreId from '../../hooks/useValidStoreId/useValidStoreId';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import AddWeightModal from './components/AddWeightModal';
import OrderSummary from './components/OrderSummary';
import 'moment/locale/es';
import 'moment/dist/locale/es';
import moment from 'moment';
import { calculateOrderTotalsFromFormValues, type OrderTotalsResult } from './utils/orderTotals';
import { useForm, FormProvider } from 'react-hook-form';
import EditQuantityModal from './components/EditQuantityModal';
import EditProductPriceModal from './components/EditProductPriceModal';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import OrderSchema, { OrderFormValues } from './order.schema';
import EditOrderHeaderAlert from './components/EditOrderHeaderAlert';
import { getDiscountCodeByIdV2 } from '../../services/store.service';
import { showNotification } from '../../utils/Notification/Notification';
import { OrderContext } from '../../context/OrderContext';
import _ from 'lodash';
import { normalizeOrderScheduleTimeValue } from '../../utils/ordersUtils';
import { AttributesPickerRow } from './components/AttributesPickerRow';
import { useNewOrderQueries } from '../../hooks/useNewOrderQueries';
import { useGetOrderById } from '../../hooks/orders/queries';
import { hydrateOperationalReturnOrderForForm } from './utils/orderHydration';
import { useGetGarmentAttributeCatalogs } from '../../hooks/company/queries';
import { getFrontendFallbackGarmentAttributeCatalogs } from '../../utils/garmentAttributeCatalogs';
import useCanEditOrders from '../../hooks/useCanEditOrders';
import useCanValidateStoreReception from '../../hooks/useCanValidateStoreReception';
import {
  STORE_RECEPTION_VALIDATION_QUERY_MODE,
  canShowValidateStoreReceptionAction
} from '../../utils/storeReceptionValidation';
import { useOrderDisplay } from '../../customerDisplay/useOrderDisplay';
const OrderLayout = () => {
  useValidStoreId();
  const { t } = useTranslation(['NewOrder']);
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const isStoreReceptionValidationMode =
    searchParams.get('mode') === STORE_RECEPTION_VALIDATION_QUERY_MODE;
  const { isUpdatingOrder, setIsUpdatingOrder, setCurrentUpdatingOrder } = useContext(OrderContext);
  const { user, company } = useSelector((state) => state.user);
  const canEditOrders = useCanEditOrders();
  const canValidateStoreReception = useCanValidateStoreReception();
  const invalidValidationModeHandledRef = useRef(false);
  const storeReceptionValidationSuccessRef = useRef(false);
  const storeReceptionValidationSubmittingRef = useRef(false);
  const initializedOrderContextRef = useRef('');

  const handleStoreReceptionValidationSubmitStart = () => {
    storeReceptionValidationSubmittingRef.current = true;
  };

  const handleStoreReceptionValidationSuccess = () => {
    storeReceptionValidationSuccessRef.current = true;
    invalidValidationModeHandledRef.current = true;
  };

  const handleStoreReceptionValidationSubmitEnd = () => {
    storeReceptionValidationSubmittingRef.current = false;
  };

  const { data: orderQuery, isLoading: isOrderLoading } = useGetOrderById(id || '', {
    fetchSection: 'true'
  });
  const returnSourceOrderId =
    typeof orderQuery?.returnSourceOrderId === 'string'
      ? orderQuery.returnSourceOrderId
      : String((orderQuery?.returnSourceOrderId as any)?._id ?? '');
  const { data: returnSourceOrderQuery, isLoading: isReturnSourceOrderLoading } =
    useGetOrderById(returnSourceOrderId, undefined, {
      enabled: Boolean(returnSourceOrderId)
    });
  const { data: garmentAttributeCatalogsData } = useGetGarmentAttributeCatalogs(
    !!company?._id,
    company?._id || '',
    {
      fallbackOnError: true,
    },
  );

  const { storeDiscounts, sections, store, cashierboxes, isAnyLoading } = useNewOrderQueries({
    orderId: id || '',
    storeId: searchParamStoreId,
    order: orderQuery
  });
  const storeReceptionValidationEnabled =
    store?.data?.storeReceptionValidationConfig?.enabled === true;

  const [searchTerm, setSearchTerm] = useState('');
  const garmentAttributeCatalogs =
    garmentAttributeCatalogsData || getFrontendFallbackGarmentAttributeCatalogs();

  const methods = useForm<OrderFormValues>({
    defaultValues: {
      buyAndGetProducts: [],
      customerInfo: null,
      delivery: false,
      pickup: false,
      express: false,
      discountCode: '',
      _id: '',
      sequence: '',
      deliveryInfo: {
        date: new Date(),
        fromTime: '',
        toTime: ''
      },
      pickupInfo: null,
      phone: '',
      email: '',
      address: null,
      notes: '',
      privateNotes: '',
      taxesType: '',
      products: [],
      creditApplied: 0,
      amountPaid: 0,
      customer: '',
      pickingUpDateTime: null,
      cleanedDateTime: null,
      readyDateTime: null,
      deliveringDateTime: null,
      collectedDateTime: null,
      cancelledDateTime: null,
      paidDateTime: null,
      status: '',
      store: '',
      paymentMethod: '',
      prepaidAmount: 0,
      prepaidPaymentMethod: '',
      notifyBy: '',
      deliveryDateFormatted: '',
      subtotal: 0,
      total: 0,
      customerName: '',
      customerDiscount: 0,
      productTotal: 0,
      taxesTotal: 0,
      productTotalWithoutDiscount: 0,
      totalDiscountAmount: 0,
      shippingServiceTotal: 0,
      discountsToApply: [],
      usePrepaid: false, //value used during order creation
      paymentLines: [],
      cashierBox: '',
      discountCodeObj: null,
      totalQuantity: 0,
      loyaltyPointsEarned: 0,
      loyaltyPointsExpectedToEarn: 0,
      loyaltyPointsRedeemed: 0,
      loyaltyDiscountAmount: 0,
      folioPrefix: null,
      orderType: 'standard',
      returnSourceOrderId: undefined,
      returnItemsChargeMode: undefined,
    },
    resolver: yupResolver(OrderSchema),
    context: {
      blockNewOrdersIfWhatsappCustomerPhoneMissing:
        store.data?.orderPageConfig?.blockNewOrdersIfWhatsappCustomerPhoneMissing ?? true,
      isUpdatingOrder,
    },
    shouldUnregister: false
  });
  const displayCalculatedTotals = useRef<OrderTotalsResult['displayTotals']>();
  const onDisplayActivity = useOrderDisplay(methods, store.data, company || {}, displayCalculatedTotals);

  const getDefaultCashierboxId = (userCashierBoxId: string, cashierboxes: any[]) => {
    //USER HAS NO CASHIERBOX so we use the first one
    if (!userCashierBoxId) {
      return cashierboxes?.[0]?._id || '';
    }

    const cashierbox = cashierboxes?.find((cb) => cb._id === userCashierBoxId);
    if (cashierbox) {
      //USER HAS A SPECIFIC CASHIERBOX in this store
      return cashierbox?._id || '';
    }

    //USER HAS A SPECIFIC CASHIERBOX in another store
    return cashierboxes?.[0]?._id || '';
  };

  const resetOrder = () => {
    displayCalculatedTotals.current = undefined;
    if (!store.data) return;
    setSearchTerm('');
    const defaultCashierBoxId = getDefaultCashierboxId(user?.cashierBox, cashierboxes?.data || []);
    let cashierBox = defaultCashierBoxId || '';
    let deliveryDate = moment(new Date())
      .add(store.data?.orderPageConfig?.defaultDeliveryDate, 'day')
      .startOf('day')
      .toDate();
    const defaultDeliveryfromTime = store.data?.orderPageConfig?.defaultDeliveryTime || '00:00';
    const defaultDeliveryTime = moment(new Date()).set({
      hour: +defaultDeliveryfromTime?.split(':')[0],
      minute: +defaultDeliveryfromTime?.split(':')[1],
      second: 0,
      millisecond: 0
    });
    const defaultNotifyMethod = store.data?.orderPageConfig?.defaultNotifyMethod || 'none';
    const defaultPaymentMethod = store.data?.paymentConfig?.defaultPaymentMethod || 'none';
    methods.reset({
      pickupInfo: undefined,
      deliveryInfo: {
        date: deliveryDate,
        fromTime: defaultDeliveryTime.format('HH:mm'),
        toTime: defaultDeliveryTime.endOf('day').format('HH:mm')
      },
      express: false,
      products: [],
      buyAndGetProducts: [],
      total: 0,
      customer: '',
      store: store.data._id || '',
      paymentMethod: defaultPaymentMethod,
      notifyBy: defaultNotifyMethod,
      pickup: false, //use to verify if it requires pickup service
      delivery: false,
      phone: '',
      email: '',
      address: null,
      notes: '',
      customerInfo: undefined,
      status: '',
      prepaidAmount: 0,
      prepaidPaymentMethod: undefined,
      customerDiscount: 0,
      productTotal: 0,
      taxesTotal: 0,
      productTotalWithoutDiscount: 0,
      totalDiscountAmount: 0,
      shippingServiceTotal: 0,
      discountsToApply: [],
      cashierBox: cashierBox,
      discountCodeObj: undefined,
      discountCode: '',
      loyaltyPointsRedeemed: 0,
      loyaltyDiscountAmount: 0,
      loyaltyPointsEarned: 0,
      loyaltyPointsExpectedToEarn: 0,
      orderType: 'standard',
      returnSourceOrderId: undefined,
      returnItemsChargeMode: undefined
    });
  };

  const calculateTotal = () => {
    const formValues = methods.getValues();
    return calculateOrderTotalsFromFormValues(
      formValues,
      store.data,
      storeDiscounts?.data ?? []
    );
  };

  const updateOrderTotal = () => {
    const totals = calculateTotal();
    displayCalculatedTotals.current = totals.displayTotals;
    const currentValues = methods.getValues();
    methods.reset(
      {
        ...currentValues,
        totalDiscountAmount: totals.totalDiscountAmount,
        totalQuantity: totals.totalQuantity,
        shippingServiceTotal: totals.shippingServiceTotal,
        taxesTotal: totals.taxesTotal,
        total: totals.total,
        productTotal: totals.productTotal,
        productTotalWithoutDiscount: totals.productTotalWithoutDiscount,
        customerDiscount: totals.customerDiscount,
        discountsToApply: Object.keys(totals.appliedOrderDiscounts).map(
          (discountId) => totals.appliedOrderDiscounts[discountId]
        ),
        subtotal: totals.subtotal
      },
      {
        keepDirty: true,
        keepErrors: true,
        keepTouched: true
      }
    );
  };

  const setOrderFromFoundOrder = async (fetchedOrder: any) => {
    try {
      if (fetchedOrder) {
        const updatingOrder = { ...fetchedOrder };

        //SET DISCOUNT CODE
        let discountCodeObj = null;
        if (fetchedOrder.appliedDiscountCodes?.length) {
          //FOR NOW WE JUST ACCEPT 1 DISCOUNT CODE PER ORDER
          let discCodeRes = await getDiscountCodeByIdV2(
            fetchedOrder.createdIn._id,
            fetchedOrder.appliedDiscountCodes[0]
          );

          if (discCodeRes.data?.data) {
            discountCodeObj = discCodeRes.data?.data;
            updatingOrder.discountCodeObj = discCodeRes.data?.data;
            updatingOrder.discountCode = discCodeRes.data?.data.code;
          }
        }
        //SET PRODUCT PROPERTIES
        for (let i = 0; i < updatingOrder.products?.length || 0; i++) {
          let product: any = updatingOrder.products[i];
          if (product.properties) {
            let newProdProps: {
              name: string;
              index: number;
              color: string[];
              damage: string;
              stain: string;
              notes: string;
            }[] = [];
            for (let j = 0; j < product?.properties?.length || 0; j++) {
              const prodProp = product?.properties[j];
              const splittedProps = prodProp.split('/');
              newProdProps.push({
                name: product.name,
                index: j,
                color: [...splittedProps[0].split(',')],
                damage: splittedProps[1],
                stain: splittedProps[2],
                notes: splittedProps[3]
              });
            }
            updatingOrder.products[i].joinedProperties = updatingOrder.products[i].properties;
            updatingOrder.products[i].properties = newProdProps;
          }
        }

        // setSelectedStore(orderFound.createdIn._id); //NO NEED TO DO IT SINCE WE CLICKED UPDATE ORDER AND WE HAVE GLOBAL STORE (CURRENT STORE) SELECTED ALREADY
        const hydratedOrder = hydrateOperationalReturnOrderForForm(updatingOrder, {
          discountCodeObj,
          storeDiscounts: storeDiscounts?.data ?? [],
          customerInfo: fetchedOrder.customer ?? null
        });
        const formatedOrderProducts = hydratedOrder.products;
        const formattedBuyAndGetProducts = hydratedOrder.buyAndGetProducts;

        //SET CUSTOMER INFO FETCHED
        updatingOrder.customerInfo = fetchedOrder.customer;

        //IF WE ARE UPDATING AN ORDER, WE ADD AGAIN THE CREDIT APPLIED TO CALCULATE TOTAL CORRECTLY
        updatingOrder.customerInfo.credit += fetchedOrder.creditApplied;

        const initialTotals = updatingOrder.orderType === 'return'
          ? calculateOrderTotalsFromFormValues(
              {
                ...updatingOrder,
                products: formatedOrderProducts,
                buyAndGetProducts: formattedBuyAndGetProducts
              },
              store.data,
              storeDiscounts?.data ?? []
            )
          : calculateTotal();
        displayCalculatedTotals.current = initialTotals.total === updatingOrder.total ? initialTotals.displayTotals : undefined;
        const {
          totalDiscountAmount,
          totalQuantity,
          shippingServiceTotal,
          productTotal,
          productTotalWithoutDiscount,
          subtotal,
          customerDiscount,
          appliedOrderDiscounts
        } = initialTotals;
        const formValuesUpdate = {
          ...updatingOrder,
          folioPrefix: fetchedOrder.folioPrefix,
          pickupInfo:
            fetchedOrder.pickupInfo && fetchedOrder.pickupInfo.date
              ? {
                  ...fetchedOrder.pickupInfo,
                  date: new Date(fetchedOrder.pickupInfo.date),
                  fromTime: normalizeOrderScheduleTimeValue(fetchedOrder.pickupInfo?.fromTime) || '00:00',
                  toTime: normalizeOrderScheduleTimeValue(fetchedOrder.pickupInfo?.toTime) || '23:59'
                }
              : {},
          deliveryInfo: fetchedOrder.deliveryInfo
            ? {
                ...fetchedOrder.deliveryInfo,
                date: new Date(fetchedOrder.deliveryInfo.date),
                fromTime: normalizeOrderScheduleTimeValue(fetchedOrder.deliveryInfo?.fromTime) || '00:00',
                toTime: normalizeOrderScheduleTimeValue(fetchedOrder.deliveryInfo?.toTime) || '23:59'
              }
            : {},
          express: updatingOrder.express,
          products: formatedOrderProducts,
          taxesType: updatingOrder.createdIn.taxesType,
          taxOne: updatingOrder.createdIn.taxOne,
          taxTwo: updatingOrder.createdIn.taxTwo,
          taxThree: updatingOrder.createdIn.taxThree,
          taxesTotal: updatingOrder.taxesTotal,
          total: updatingOrder.total,
          customer: fetchedOrder.customer?._id || undefined,
          customerInfo: updatingOrder.customer,
          store: fetchedOrder.createdIn._id,
          notifyBy: updatingOrder.notifyBy,
          discountCode: discountCodeObj?._id,
          discountCodeObj: discountCodeObj || undefined,
          notes: updatingOrder.notes,
          totalDiscountAmount,
          totalQuantity,
          shippingServiceTotal,
          productTotal,
          productTotalWithoutDiscount,
          subtotal,
          customerDiscount,
          attributes: fetchedOrder.attributes,
          discountsToApply: Object.keys(appliedOrderDiscounts).map((discountId) => {
            return appliedOrderDiscounts[discountId];
          }),
          ///VERIFY
          // discountsToApply: fetchedOrder.appliedStoreDiscounts,
          buyAndGetProducts: formattedBuyAndGetProducts,
          status: fetchedOrder.status,
          cashierBox: '' //this is not editable
        };
        methods.reset(formValuesUpdate);
        const cloned = _.cloneDeep(formValuesUpdate);
        setCurrentUpdatingOrder(cloned);
      }
    } catch (e) {
      showNotification({
        title: 'Error',
        message: e?.message || 'No pudimos actualizar la orden',
        type: 'Error'
      });
    } finally {
      setIsUpdatingOrder(true);
    }
  };

  useEffect(() => {
    if (!store.data || !cashierboxes?.data || storeDiscounts.isLoading) return;
    if (id && !orderQuery) return;

    const initializationKey = `${id || 'new'}:${store.data._id || ''}`;
    if (initializedOrderContextRef.current === initializationKey) return;
    initializedOrderContextRef.current = initializationKey;

    // If we have an order ID and order data, initialize with order data
    if (id && orderQuery) {
      setOrderFromFoundOrder(orderQuery);
    } else {
      // If no order ID, reset to default values
      resetOrder();
      setIsUpdatingOrder(false);
    }
  }, [store.data, cashierboxes?.data, storeDiscounts.data, storeDiscounts.isLoading, orderQuery, id]);

  useEffect(() => {
    if (!id || !orderQuery || invalidValidationModeHandledRef.current) return;
    if (isStoreReceptionValidationMode && !store?.data) return;

    const canValidateCurrentOrder = canShowValidateStoreReceptionAction({
      order: orderQuery,
      canValidateStoreReception,
      storeReceptionValidationEnabled
    });

    if (
      isStoreReceptionValidationMode &&
      !canValidateCurrentOrder &&
      !storeReceptionValidationSubmittingRef.current &&
      !storeReceptionValidationSuccessRef.current
    ) {
      invalidValidationModeHandledRef.current = true;
      showNotification({
        title: t('NewOrder:storeReceptionValidation.unavailableTitle'),
        message: t('NewOrder:storeReceptionValidation.unavailableMessage'),
        type: 'Error'
      });
      history.goBack();
      return;
    }

    if (!isStoreReceptionValidationMode && !canEditOrders) {
      invalidValidationModeHandledRef.current = true;
      showNotification({
        title: t('NewOrder:noEditPermissionTitle'),
        type: 'Error'
      });
      history.goBack();
    }
  }, [
    canEditOrders,
    canValidateStoreReception,
    history,
    id,
    isStoreReceptionValidationMode,
    orderQuery,
    store?.data,
    storeReceptionValidationEnabled,
    t
  ]);

  //set default section
  useEffect(() => {
    if (!sections?.data) return;
    const params = new URLSearchParams(location.search);
    params.set('section', (sections.data || [])[0]?._id || '');
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [sections.data]);

  // Watch specific fields directly without providing initial values
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      // Only update totals when relevant fields change
      const relevantFields = [
        'products',
        'buyAndGetProducts',
        'delivery',
        'pickup',
        'express',
        'customerInfo',
        'discountCode',
        'discountCodeObj',
        'loyaltyPointsRedeemed',
        'loyaltyDiscountAmount'
      ];

      if (name && relevantFields.includes(name)) {
        //Validate if is discount code or discount code obj it should both be present
        if (['discountCodeObj', 'discountCode'].includes(name) && !value.discountCodeObj) {
          return;
        }
        updateOrderTotal();
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, store?.data, storeDiscounts?.data]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  if (isAnyLoading || isOrderLoading || isReturnSourceOrderLoading) {
    return <LoaderOverlay visible />;
  }

  return (
    <div onPointerDownCapture={onDisplayActivity} onKeyDownCapture={onDisplayActivity} onInputCapture={onDisplayActivity}>
    <FormProvider {...methods}>
      <Grid p={'md'}>
        <AddWeightModal storeDiscounts={storeDiscounts?.data} />
        <EditQuantityModal storeDiscounts={storeDiscounts?.data} />
        <EditProductPriceModal store={store.data} storeDiscounts={storeDiscounts?.data} />
        <Grid.Col span={{ base: 12, md: 7 }} style={{ position: 'relative' }}>
          <Grid>
            <Grid.Col
              span={12}
              pb={'md'}
              style={{
                minHeight: '100vh',
                overflow: 'auto'
              }}>
              {isUpdatingOrder && (
                <EditOrderHeaderAlert
                  order={orderQuery}
                  sourceOrder={returnSourceOrderQuery}
                />
              )}
              {isStoreReceptionValidationMode && (
                <Alert color="blue" mb="md">
                  {t('NewOrder:storeReceptionValidation.banner')}
                </Alert>
              )}
              <ProductSearcher
                sections={sections.data || []}
                onSearch={handleSearch}
                searchTerm={searchTerm}
                storeDiscounts={storeDiscounts?.data}
              />
              <ProductList
                sections={sections.data || []}
                productSearchTerm={searchTerm}
                storeDiscounts={storeDiscounts?.data}
                isLoadingSections={sections.isLoading}
                refetchSections={sections?.refetch}
              />
            </Grid.Col>
            <Grid.Col
              span={12}
              style={{
                position: 'sticky',
                bottom: 'calc(calc(1rem * 1) / 2)',
                backgroundColor: 'white',
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
                borderRadius: '10px 10px',
                zIndex: 10
              }}>
              <AttributesPickerRow garmentAttributeCatalogs={garmentAttributeCatalogs} />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <OrderSummary
            cashierboxes={cashierboxes?.data || []}
            sections={sections?.data || []}
            storeDiscounts={storeDiscounts?.data}
            resetOrder={resetOrder}
            store={store?.data}
            garmentAttributeCatalogs={garmentAttributeCatalogs}
            isStoreReceptionValidationMode={isStoreReceptionValidationMode}
            onStoreReceptionValidationSubmitStart={handleStoreReceptionValidationSubmitStart}
            onStoreReceptionValidationSuccess={handleStoreReceptionValidationSuccess}
            onStoreReceptionValidationSubmitEnd={handleStoreReceptionValidationSubmitEnd}
          />
        </Grid.Col>
      </Grid>
    </FormProvider>
    </div>
  );
};

export default OrderLayout;
